import { useState, useEffect, type FC } from 'react';
import { Modal, Input, Select, Switch } from 'antd';
import { Edit } from 'lucide-react';
import type { KnowledgeBrick } from './KnowledgeBrickTable';

interface Props {
  brick: KnowledgeBrick | null;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (id: string, values: Partial<KnowledgeBrick>) => void;
}

const CATEGORIES = [
  { value: 'argument_vente', label: 'Argument de vente' },
  { value: 'case_study', label: 'Case study' },
  { value: 'technical_spec', label: 'Spécification technique' },
  { value: 'testimonial', label: 'Témoignage' },
  { value: 'competitive_advantage', label: 'Avantage concurrentiel' },
  { value: 'faq', label: 'FAQ' },
  { value: 'objection_response', label: 'Réponse à objection' },
];

const PERSONAS = [
  { value: 'cto', label: 'CTO' },
  { value: 'po', label: 'PO' },
  { value: 'ceo', label: 'CEO' },
  { value: 'qa-lead', label: 'QA Lead' },
];

const KnowledgeBrickEditModal: FC<Props> = ({ brick, open, saving, onClose, onSave }) => {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [personas, setPersonas] = useState<string[]>([]);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (brick) {
      setContent(brick.content);
      setCategory(brick.category);
      setPersonas(
        Array.isArray(brick.personaTarget)
          ? brick.personaTarget
          : brick.personaTarget
            ? [brick.personaTarget]
            : [],
      );
      setIsVerified(brick.isVerified);
    }
  }, [brick]);

  const handleSave = () => {
    if (!brick) return;
    onSave(brick.id, {
      content,
      category,
      personaTarget: personas,
      isVerified,
    });
  };

  const hasChanges =
    brick &&
    (content !== brick.content ||
      category !== brick.category ||
      isVerified !== brick.isVerified ||
      JSON.stringify(personas) !==
        JSON.stringify(
          Array.isArray(brick.personaTarget)
            ? brick.personaTarget
            : brick.personaTarget
              ? [brick.personaTarget]
              : [],
        ));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center gap-2">
          <Edit className="w-5 h-5 text-amber-500" />
          <span>Modifier la brique</span>
        </div>
      }
      okText="Sauvegarder"
      cancelText="Annuler"
      onOk={handleSave}
      okButtonProps={{ disabled: !hasChanges || saving, loading: saving }}
      width={640}
      destroyOnClose
    >
      <div className="space-y-4 pt-2">
        {/* Content */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400">
            Contenu
          </label>
          <Input.TextArea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
            placeholder="Contenu de la brique de connaissance..."
            showCount
            maxLength={2000}
          />
        </div>

        {/* Category */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400">
            Catégorie
          </label>
          <Select
            value={category}
            onChange={setCategory}
            options={CATEGORIES}
            className="w-full"
          />
        </div>

        {/* Personas */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400">
            Persona(s) cible
          </label>
          <Select
            mode="multiple"
            value={personas}
            onChange={setPersonas}
            options={PERSONAS}
            className="w-full"
            placeholder="Sélectionner les personas..."
          />
        </div>

        {/* Verified */}
        <div className="flex items-center gap-3">
          <Switch checked={isVerified} onChange={setIsVerified} />
          <span className="text-sm text-slate-700 dark:text-slate-300">
            {isVerified ? 'Vérifié' : 'Non vérifié'}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default KnowledgeBrickEditModal;
